import React from "react";
import { ControllerRenderProps, useFormContext } from "react-hook-form";
import { Typography } from "./ui/typography";
import { FormField, FormItem, FormMessage, FormInput } from "./ui/form";
import StackFormLayout from "./ui/stack-form-layout";
import StackSelectionCard from "./ui/stack-selection-card";
import StackFormButtons from "./ui/stack-form-buttons";
import BalanceDisplay from "./ui/balance-display";
import {
  StackMoreFormData,
  StackMoreStepType as StepType,
} from "../pages/StackMore";
import stackMoreWalletIcon from "../assets/images/autostack/stack_more_wallet.svg";
import stackMoreBankIcon from "../assets/images/autostack/stack_more_bank.svg";
import { useQueryDigitalWallet } from "../hooks/query/queries";
import {
  formatCurrency,
  formatNumberWithCommas,
  parseFormattedNumber,
} from "../utils/utils";

interface StackMoreStepProps {
  onStepComplete: () => void;
  onStepBack: () => void;
  onCancel: () => void;
  currentStep: StepType;
}

type StackAmountField = ControllerRenderProps<
  {
    stackAmount: string;
    stackType: "wallet" | "bank";
  },
  "stackAmount"
>;

const StackMoreStep: React.FC<StackMoreStepProps> = ({
  onStepComplete,
  onCancel,
}) => {
  const form = useFormContext<StackMoreFormData>();

  const { digitalWalletData, isLoading: isLoadingDigitalWallet } =
    useQueryDigitalWallet();

  const fiatBalance =
    digitalWalletData?.balances?.assetBalances?.fiat?.[0]?.availableAmount || 0;

  const processValue = (value: string): string => {
    const rawValue = parseFormattedNumber(value);
    return rawValue && Number(rawValue) > 0 ? rawValue : "";
  };

  const handleAmountChange = (value: string, field: StackAmountField) => {
    const processedValue = processValue(value);
    if (processedValue || value === "") {
      field.onChange(processedValue);
    }
  };

  const handleAmountBlur = (value: string, field: StackAmountField) => {
    const processedValue = processValue(value);
    field.onChange(processedValue);
    field.onBlur();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onStepComplete();
  };

  return (
    <StackFormLayout title="Stack More" showCancelHeader={false}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          control={form.control}
          name="stackAmount"
          render={({ field }) => (
            <FormItem>
              <Typography variant="label" className="block mb-3">
                How much would you like to stack?
              </Typography>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  R
                </span>
                <FormInput
                  type="text"
                  placeholder="5,000"
                  className="h-14 pl-8"
                  value={formatNumberWithCommas(field.value || "")}
                  onChange={(e) => handleAmountChange(e.target.value, field)}
                  onBlur={(e) => handleAmountBlur(e.target.value, field)}
                  name={field.name}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="stackType"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-wrap md:flex-nowrap gap-4">
                <StackSelectionCard
                  isSelected={field.value === "wallet"}
                  onClick={() => field.onChange("wallet")}
                  title="Wallet"
                  description="Stack more via your wallet"
                  icon={stackMoreWalletIcon}
                  iconBgColor="bg-pink-100"
                  variant="compact"
                  footer={
                    <BalanceDisplay
                      amount={formatCurrency(fiatBalance, "ZAR")}
                      variant="compact"
                      isLoading={isLoadingDigitalWallet}
                    />
                  }
                />

                <StackSelectionCard
                  isSelected={field.value === "bank"}
                  onClick={() => field.onChange("bank")}
                  title="Bank Transfer"
                  description="Stack more via a bank transfer"
                  icon={stackMoreBankIcon}
                  iconBgColor="bg-purple-100"
                  variant="compact"
                  disabled={true}
                />
              </div>
            </FormItem>
          )}
        />

        <StackFormButtons
          onBack={onCancel}
          isSubmitType={true}
          variant="rounded"
        />
      </form>
    </StackFormLayout>
  );
};

export default StackMoreStep;
